export type Role = "product" | "target";
export type RoleMap = Record<string, Role>;

interface Identified {
  id: string;
}

/**
 * Cycles a single image through: none -> product -> target -> none.
 * Assigning "target" clears it from whichever image held it, so at most
 * one target ever exists.
 */
export function cycleRole(roles: RoleMap, imageId: string): RoleMap {
  const current = roles[imageId];
  const next: Role | undefined =
    current === undefined ? "product" : current === "product" ? "target" : undefined;

  const out: RoleMap = { ...roles };
  delete out[imageId];

  if (next === "target") {
    for (const key of Object.keys(out)) {
      if (out[key] === "target") delete out[key];
    }
    out[imageId] = "target";
  } else if (next === "product") {
    out[imageId] = "product";
  }

  return out;
}

/**
 * Drops roles for images that no longer exist and seeds sensible defaults
 * (first = product, last = target) — making the previously implicit
 * "last reference image is the target" convention visible.
 *
 * Never overwrites a role the user set by hand, and never seeds when there
 * is only one image, where roles are meaningless.
 */
export function syncRoles<T extends Identified>(images: T[], roles: RoleMap): RoleMap {
  const ids = new Set(images.map((img) => img.id));
  const pruned: RoleMap = {};
  for (const [id, role] of Object.entries(roles)) {
    if (ids.has(id)) pruned[id] = role;
  }

  if (images.length < 2) return pruned;

  const assigned = Object.values(pruned);
  const first = images[0]!;
  const last = images[images.length - 1]!;

  if (!assigned.includes("product") && !pruned[first.id]) {
    pruned[first.id] = "product";
  }
  if (!Object.values(pruned).includes("target") && !pruned[last.id]) {
    pruned[last.id] = "target";
  }

  return pruned;
}

/**
 * Reorders images so the model receives them in the conventional order
 * (source garments first, target person last) and builds a sentence that
 * names each one by its position in that new order.
 *
 * With no target assigned nothing is changed — the caller behaves exactly
 * as it did before roles existed.
 */
export function applyTransferRoles<T extends Identified>(
  images: T[],
  roles: RoleMap
): { ordered: T[]; sentence: string } {
  const target = images.find((img) => roles[img.id] === "target");
  if (!target) return { ordered: images, sentence: "" };

  const products = images.filter((img) => roles[img.id] === "product");
  const neutral = images.filter((img) => !roles[img.id]);
  const ordered = [...products, ...neutral, target];

  if (products.length === 0) return { ordered, sentence: "" };

  const targetPos = ordered.length;
  const subject =
    products.length === 1
      ? "Image 1 is the source garment."
      : `Images 1-${products.length} are the source garments.`;
  const ref = products.length === 1 ? "image 1" : `images 1-${products.length}`;

  const sentence =
    `${subject} Image ${targetPos} is the target person. ` +
    `Transfer the garment from ${ref} onto the person in image ${targetPos}, ` +
    `replacing what they currently wear.`;

  return { ordered, sentence };
}
